// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

// api
import type { GetEnergyTreeNodeDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import { CancelButton } from '@/components/ui/button';
import { ConfirmButton } from '@/components/ui/button/ConfirmButton';

interface ValidationIssue {
  error_type?: string;
  warning_type?: string;
  message: string;
  device_ids?: string[];
}

interface ValidationResult {
  Status?: string;
  Name?: string;
  Errors?: ValidationIssue[];
  Warnings?: ValidationIssue[];
}

interface SaveChangesDialogProps {
  open: boolean;
  onClose: () => void;
  validationResult?: ValidationResult;
  validationError?: unknown;
  isSaving: boolean;
  onConfirmSave: () => void;
  draftTree?: GetEnergyTreeNodeDto[];
}

const SaveChangesDialog = ({
  open,
  onClose,
  validationResult,
  validationError,
  isSaving,
  onConfirmSave,
  draftTree,
}: SaveChangesDialogProps) => {
  const { t } = useTranslation('energyxplorer-tree-tab');

  const validationStatus = validationResult?.Status;
  const validationSuccessful = validationStatus === 'Success';
  const hasIssues =
    validationStatus === 'Error' ||
    validationStatus === 'Warning' ||
    validationStatus === 'Critical';

  // Helper function to get node names from device_ids
  const getNodeNamesFromDeviceIds = (deviceIds?: string[]): string => {
    if (!deviceIds || !Array.isArray(deviceIds) || deviceIds.length === 0) {
      return '';
    }
    if (!draftTree) {
      return '';
    }

    return deviceIds
      .map(
        (deviceId) =>
          draftTree.find((node) => node.energyTreeNodeId === deviceId)?.name,
      )
      .filter(Boolean)
      .join(', ');
  };

  const renderIssueList = (
    issues: ValidationIssue[],
    severity: 'error' | 'warning',
  ) =>
    issues.map((issue, index) => {
      const issueType = issue.error_type || issue.warning_type;
      const nodeName = getNodeNamesFromDeviceIds(issue.device_ids);
      const translatedTitle = issueType
        ? t(`tree_validation.validation_rule.${issueType}.title`, {
            nodeName,
            defaultValue: issue.message,
          })
        : issue.message;
      const translatedMessage = issueType
        ? t(`tree_validation.validation_rule.${issueType}.message`, {
            nodeName,
            defaultValue: issue.message,
          })
        : issue.message;
      const translatedAction = issueType
        ? t(`tree_validation.validation_rule.${issueType}.action`, {
            nodeName: nodeName || '',
            defaultValue: '',
          })
        : '';

      return (
        <Alert key={`${severity}-${index}`} severity={severity}>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {translatedTitle}
            </Typography>
            <Typography variant="body2">{translatedMessage}</Typography>
            {translatedAction && (
              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                {translatedAction}
              </Typography>
            )}
          </Stack>
        </Alert>
      );
    });

  return (
    <Dialog
      data-testid="orgchart-save-changes-dialog"
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle data-testid="save-dialog-title">
        {t('tree_actions.save_changes')}
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: '70vh' }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('tree_validation.save_changes_description')}
          </Typography>

          {validationError && (
            <Alert severity="error">
              {t('tree_validation.validation_error')}
            </Alert>
          )}

          {hasIssues && (
            <>
              <Divider />

              {!validationSuccessful && (
                <Typography variant="body2" color="text.secondary">
                  <b>{t('tree_validation.validation_failed')}</b>{' '}
                  {t('tree_validation.save_changes_with_errors')}
                </Typography>
              )}
              {validationResult?.Errors?.length &&
                validationResult.Errors.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">
                      {t('tree_validation.errors')}
                    </Typography>
                    <Stack spacing={1} data-testid="save-dialog-error-list">
                      {renderIssueList(validationResult.Errors, 'error')}
                    </Stack>
                  </Stack>
                )}

              {validationResult?.Warnings?.length &&
                validationResult.Warnings.length > 0 && (
                  <Stack spacing={1}>
                    <Stack spacing={1} data-testid="save-dialog-warning-list">
                      {renderIssueList(validationResult.Warnings, 'warning')}
                    </Stack>
                  </Stack>
                )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <CancelButton onClick={onClose} disabled={isSaving}>
          {t('tree_actions.cancel')}
        </CancelButton>

        <ConfirmButton
          data-testid="save-dialog-confirm-button"
          onClick={onConfirmSave}
        >
          {isSaving ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              {t('tree_actions.saving')}
            </Box>
          ) : (
            t('tree_actions.save_changes')
          )}
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};

export default SaveChangesDialog;
